import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Listbox } from "@headlessui/react";

// Components
import TableControls from "@surgia/ui/containers/table/table-controls";
import { GenericTableHeader } from "@surgia/ui";
import BranchesTable from "../../../containers/tables/branches-table";
import GenericModal from "@surgia/ui/components/genericModal";
import Button from "@surgia/ui/components/button";
import AssignBranchForm from "@surgia/ui/containers/assignBranchForm";

// Queries
import { branchQueries } from "@surgia/api/src/queryStore/shared/branch";
import { lookupQueries } from "@surgia/api/src/queryStore/shared/lookup";
import { tenantQueries as adminTenantQueries } from "@surgia/api/src/queryStore/admin/tenant";

// Icons

// Constants
const ITEMS_PER_PAGE = 8;

// City Select Component
interface City {
  id: number;
  name: string;
}

interface CitySelectProps {
  cities: City[];
  selectedCity: number | null;
  onCityChange: (cityId: number | null) => void;
  isLoading?: boolean;
  className?: string;
  placeholder?: string;
}

const CitySelect: React.FC<CitySelectProps> = ({
  cities,
  selectedCity,
  onCityChange,
  isLoading = false,
  className = "w-full h-10 px-3 py-2 text-sm border border-[#D5D7DA] rounded-lg bg-white focus:outline-none",
}) => {
  const selectedCityObj =
    cities.find((city) => city.id === selectedCity) || null;

  return (
    <div className="flex flex-col gap-2">
      <Listbox
        value={
          !selectedCityObj
            ? {
                id: null,
                name: "All Cities",
              }
            : selectedCityObj
        }
        onChange={(city) => {
          onCityChange(city?.id || null);
        }}
      >
        <div className="relative">
          <Listbox.Button
            disabled={isLoading}
            className={`${className} ${
              isLoading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            <span className="block truncate text-left">
              {selectedCityObj ? selectedCityObj.name : "All Cities"}
            </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <svg
                className="w-5 h-5 text-gray-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          </Listbox.Button>

          <Listbox.Options className="absolute z-[1000] mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
            <Listbox.Option
              key="all"
              value={null}
              className={({ active }) =>
                `relative cursor-default select-none py-2 pl-10 pr-4 ${
                  active ? "bg-blue-100 text-blue-900" : "text-gray-900"
                }`
              }
            >
              {({ selected }) => (
                <>
                  <span
                    className={`block truncate ${
                      !selected && !selectedCityObj
                        ? "font-medium"
                        : "font-normal"
                    }`}
                  >
                    All Cities
                  </span>
                  {!selected && !selectedCityObj ? (
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                      <svg
                        className="w-5 h-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  ) : null}
                </>
              )}
            </Listbox.Option>
            {cities.map((city) => (
              <Listbox.Option
                key={city.id}
                value={city}
                className={({ active }) =>
                  `relative cursor-default select-none py-2 pl-10 pr-4 ${
                    active ? "bg-blue-100 text-blue-900" : "text-gray-900"
                  }`
                }
              >
                {({ selected }) => (
                  <>
                    <span
                      className={`block truncate ${selected ? "font-medium" : "font-normal"}`}
                    >
                      {city.name}
                    </span>
                    {selected ? (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                        <svg
                          className="w-5 h-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </div>
      </Listbox>
    </div>
  );
};

// Filters Modal Component
export const FiltersModal = ({
  open,
  onClose,
  onApply,
  filters,
  setFilters,
  cities,
}: {
  open: boolean;
  onClose: () => void;
  onApply: () => void;
  filters: any;
  setFilters: (f: any) => void;
  cities: any[];
}) => {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = React.useState(filters);

  // Keep localFilters in sync with parent filters when modal opens
  React.useEffect(() => {
    if (open) setLocalFilters(filters);
  }, [open, filters]);

  const handleApply = () => {
    setFilters(localFilters);
    onApply();
  };

  const handleClear = () => {
    setLocalFilters({
      ...localFilters,
      city_id: "",
    });
  };

  return (
    <GenericModal
      title={t("filters")}
      open={open}
      onClose={onClose}
      additionalStyles="!overflow-visible"
      footer={
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={handleClear}
            className="text-[#0033CC] font-medium"
          >
            {t("admin.clearFilters")}
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button variant="primary" onClick={handleApply}>
            {t("filter")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* City Filter */}
        <div>
          <label className="block text-[14px] mb-1">
            {t("shared.table.city")}
          </label>
          <CitySelect
            cities={cities}
            selectedCity={localFilters.city_id || null}
            onCityChange={(cityId) => {
              setLocalFilters({ ...localFilters, city_id: cityId || "" });
            }}
          />
        </div>
      </div>
    </GenericModal>
  );
};

function Branches() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { id: tenantId } = useParams<{ id: string }>();

  // State management
  const [currentPage, setCurrentPage] = useState(0);
  const [openFilters, setOpenFilters] = useState(false);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    city_id: "",
  });
  const [showCreateBranchModal, setShowCreateBranchModal] = useState(false);

  // Add sorting state with initial sort by ID descending (newest first)
  const [sortState, setSortState] = useState<{
    column: string | null;
    direction: "asc" | "desc" | null;
  }>({
    column: "id", // Initial sort column
    direction: "desc", // Initial sort direction (desc for newest first)
  });

  // Fetch tenant data to get country_id
  const { data: tenantResponse } = useQuery(
    adminTenantQueries.queries.tenants({
      status: "ACTIVE",
      id: +tenantId!,
    })
  );

  const countryId = tenantResponse?.data[0]?.country?.id;

  // Fetch cities data
  const { data: citiesResponse } = useQuery({
    ...lookupQueries.queries.cities({
      size: 1000,
      country_id: countryId!,
    }),
    enabled: !!countryId, // Only fetch when countryId is available
  });

  const cities = citiesResponse?.data || [];

  // Fetch branches data for this specific tenant
  const { data: branchesResponse, isLoading } = useQuery({
    ...branchQueries.queries.getBranches({
      tenant_id: parseInt(tenantId!),
      component_id: import.meta.env.VITE_PUBLIC_COMPONENT_ID,
      name: search,
      size: ITEMS_PER_PAGE,
      page: currentPage,
      city_id: filters.city_id || "",
      sort:
        sortState.column && sortState.direction
          ? `${sortState.column},${sortState.direction}`
          : undefined,
    }),
    select: (data: any) => ({
      total: data?.total || 0,
      data: data?.data?.map((branch: any) => ({
        ...branch,
        city: branch.address?.city?.name,
        area: branch.address?.area?.name,
      })),
    }),
  });

  const branches = branchesResponse?.data || [];
  const total = branchesResponse?.total || 0;

  useEffect(() => {
    setCurrentPage(0);
  }, [search, filters.city_id]);

  // Handle sort changes
  const handleSort = (newSortState: {
    column: string | null;
    direction: "asc" | "desc" | null;
  }) => {
    setSortState(newSortState);
    setCurrentPage(0); // Reset to first page when sorting changes
  };

  const handleSuccessfullyCreatedBranch = () => {
    setShowCreateBranchModal(false);
    // The query will automatically refetch due to React Query's cache invalidation
  };

  return (
    <div dir={isRTL ? "rtl" : "ltr"}>
      <div className="bg-white rounded-xl border border-gray-200 shadow pt-4">
        <GenericTableHeader
          title="Branches"
          totalCount={total || 0}
          showBadge={true}
          customBadgeText={
            total != undefined
              ? `${total} ${total === 1 ? "branch" : "branches"}`
              : "0 branches"
          }
          layout="justified"
          borderStyle="gray-100"
          padding="px-4"
        >
          <div className="flex items-center">
            <TableControls
              statusTabs={[]}
              status=""
              setStatus={() => {}}
              search={search}
              setSearch={setSearch}
              openFilters={openFilters}
              setOpenFilters={setOpenFilters}
              showFilter={true}
            />
          </div>
        </GenericTableHeader>

        {/* Branches Table Section */}
        <BranchesTable
          data={branches}
          currentPage={currentPage}
          totalPages={Math.ceil(total / ITEMS_PER_PAGE)}
          onPageChange={setCurrentPage}
          loading={isLoading}
          hasRequiredEditPermission={true}
          sortState={sortState}
          onSort={handleSort}
        />
      </div>

      {/* Create Branch Modal */}
      {showCreateBranchModal && countryId && (
        <GenericModal
          title="Add Branch"
          open={showCreateBranchModal}
          onClose={() => setShowCreateBranchModal(false)}
          footer={undefined}
          additionalStyles="!overflow-visible"
        >
          <AssignBranchForm
            createBranch={true}
            createTenantBranch={true}
            onSuccess={handleSuccessfullyCreatedBranch}
            requestId={+tenantId!}
            onClose={() => setShowCreateBranchModal(false)}
            countryId={countryId}
          />
        </GenericModal>
      )}

      {/* Filters Modal */}
      {openFilters && (
        <FiltersModal
          open={openFilters}
          onClose={() => setOpenFilters(false)}
          onApply={() => setOpenFilters(false)}
          filters={filters}
          setFilters={setFilters}
          cities={cities}
        />
      )}
    </div>
  );
}

export default Branches;
